/**
 * 장비 아이콘과 아이템 설명 창.
 *
 * 플레이한 사람이 청했다: "모든 아이템 아이콘을 그려서 ui에 배치해줘. 전리품 아이템 섹션은 별도로 만들어서 눈에
 * 잘 띄게하자. 아이템을 터치하면 툴팁을 띄워주고."
 *
 * 아이콘은 한 장의 시트(`public/sprites/items.png`)에 `EQUIPMENT` 순서대로 32픽셀 칸으로 늘어서 있다. 순서가 곧
 * 자리라 따로 색인 표를 두지 않는다 — 장비를 더하면 `tools/items_pack.py`로 시트를 다시 굽는다(`tests/items.test.ts`가
 * 시트 크기와 장비 수가 맞는지 본다).
 */
import { EQUIPMENT, SLOT_NAMES, Source, heroStats } from '../sim/equipment.ts';
import type { EquipDef } from '../sim/equipment.ts';
import { CULTURE_NAMES, WEAPONS } from '../sim/troops.ts';
import type { Stats } from '../sim/troops.ts';
import { TRAIT_BY_ID } from '../sim/traits.ts';
import { el } from './dom.ts';

/** 시트의 칸 크기(픽셀)와 한 줄의 칸 수. `tools/items_pack.py`와 같아야 한다. */
export const ITEM_CELL = 32;
export const ITEM_COLS = 8;

const INDEX = new Map(EQUIPMENT.map((e, k) => [e.id, k]));

/**
 * 아이콘 한 칸. `size`는 화면 픽셀(32의 배수가 선명하다). 티어는 테두리 색으로 보인다(`.item-icon.t3` 등).
 * 도트 그림이라 확대할 때 흐려지지 않게 `image-rendering: pixelated`로 그린다(CSS).
 */
export function itemIcon(item: EquipDef, size = 32): HTMLElement {
  // 강화한 물건(`EquipItem`)의 id는 열쇠(`w_x+2`)다 — 아이콘은 바탕 물건 칸.
  const k = INDEX.get((item as { baseId?: string }).baseId ?? item.id) ?? 0;
  const scale = size / ITEM_CELL;
  const col = k % ITEM_COLS;
  const row = Math.floor(k / ITEM_COLS);
  const rows = Math.ceil(EQUIPMENT.length / ITEM_COLS);
  const span = el('span', { class: `item-icon t${item.tier}`, 'aria-hidden': 'true' });
  span.style.width = `${size}px`;
  span.style.height = `${size}px`;
  span.style.backgroundSize = `${ITEM_COLS * ITEM_CELL * scale}px ${rows * ITEM_CELL * scale}px`;
  span.style.backgroundPosition = `${-col * ITEM_CELL * scale}px ${-row * ITEM_CELL * scale}px`;
  // 강화 단계를 아이콘 귀퉁이에 적는다. 이름 없이 아이콘만 보이는 자리(결과 화면의 전리품·사라진 장비)에서 단계가
  // 보이지 않았다(QA) — 강화한 물건을 잃은 것이 한눈에 보여야 한다.
  const star = (item as { star?: number }).star ?? 0;
  if (star > 0) span.append(el('i', { class: 'item-star' }, `★${star}`));
  return span;
}

const MOD_NAMES: Record<string, string> = {
  hp: '체력', atk: '공격', def: '방어', range: '사거리',
  speed: '속도', radius: '반경', mass: '질량', grit: '사기', pierce: '관통',
};

/**
 * 보정 목록. 간격(`cd`)은 공격과 공격 사이의 틱(30틱 = 1초)이라 "간격 −1"로는 뜻이 안 읽혔다
 * (플레이한 사람이 "간격이 뭐지? 공격 속도인가?"라고 물었다). 찰 사람을 알면 공격 속도 %를 적는다.
 *
 * swap: 고른 사람의 [지금 착장, 이것으로 바꿔 찬 착장] 몸. 무기는 성격을 비율로 옮기므로(`rearm`) 같은 철부라도
 * 사람·레벨마다 오르는 공격이 다르다 — 무기는 모든 수치를 이 한 비교로 적는다. 처음에는 공격 등은 맨몸 장수 기준,
 * 공격 속도만 고른 사람 기준이라 한 줄에 기준이 섞였다(리뷰가 짚었다).
 */
export function modSummary(item: EquipDef, swap?: [Stats, Stats]): string {
  const parts: string[] = [];
  // 무기는 무기 종류의 성격(`rearm`)과 품질(`mods`)이 합쳐져 든다. 둘을 따로 적으면 "공격 +10"인데 실제로는 느려지는
  // 창처럼 읽는 사람이 합을 해야 한다 — 맨몸 장수에게 들렸을 때의 차이를 한 번에 적는다.
  if (item.weapon) {
    // 고른 사람이 없으면(상점 설명 창) 맨몸 장수에게 들렸을 때다. 그렇다고 적는다.
    const [now, next] = swap ?? [heroStats({}), heroStats({ weapon: item.id })];
    parts.push(swap ? WEAPONS[item.weapon].name : `${WEAPONS[item.weapon].name}(맨몸 장수 기준)`);
    for (const k of ['atk', 'def', 'pierce', 'range'] as const) {
      const d = next[k] - now[k];
      if (d !== 0) parts.push(`${MOD_NAMES[k]} ${d > 0 ? '+' : ''}${d}`);
    }
    if (now.cd !== next.cd && next.cd > 0) {
      const pct = Math.round((now.cd / next.cd - 1) * 100);
      parts.push(`공격 속도 ${pct > 0 ? '+' : ''}${pct}%`);
    }
  }
  for (const [k, v] of Object.entries(item.weapon ? {} : item.mods)) {
    if (k === 'cd') {
      if (swap && swap[1].cd > 0) {
        const pct = Math.round((swap[0].cd / swap[1].cd - 1) * 100);
        parts.push(`공격 속도 ${pct > 0 ? '+' : ''}${pct}%`);
      } else {
        parts.push(`공격 ${v < 0 ? '빨라짐' : '느려짐'}`);
      }
      continue;
    }
    parts.push(`${MOD_NAMES[k] ?? k} ${v > 0 ? '+' : ''}${v}`);
  }
  // 방패의 성능은 막는 화살 수다. 처음에는 적지 않아 방패 넷이 모두 "보정 없음"으로 보였다(리뷰가 짚었다).
  if (item.blocks) parts.push(`앞쪽 화살 ${item.blocks}발 막음`);
  if (item.command?.gritBonus) parts.push(`휘하 사기 +${item.command.gritBonus}`);
  if (item.command?.atkPct) parts.push(`휘하 공격 +${item.command.atkPct}%`);
  if (item.command?.defBonus) parts.push(`휘하 방어 +${item.command.defBonus}`);
  // 특성 짝 장비: 휘하 궁수가 그 날씨의 벌점을 받지 않는다.
  for (const t of item.weatherproof ?? []) parts.push(`휘하 궁수 ${TRAIT_BY_ID.get(t)?.name ?? t}에도 그대로`);
  return parts.join(' · ') || '보정 없음';
}

/** 전리품(깊이에서만 나오는 것, 시즌 보상)인가. 상점에서 사는 것과 가른다. */
export function isLoot(item: EquipDef): boolean {
  return item.source === Source.Depth || item.source === Source.Season;
}

let open: { box: HTMLElement; anchor: HTMLElement; off: () => void } | null = null;

/** 열린 설명 창을 닫는다. 화면을 다시 그릴 때도 부른다 — 붙어 있던 줄이 사라지면 창만 떠 있게 된다. */
export function closeItemTip(): void {
  if (!open) return;
  open.off();
  open.box.remove();
  open = null;
}

/**
 * 아이템 설명 창을 `anchor` 아래(자리가 없으면 위)에 띄운다. 같은 것을 다시 누르면 닫는다. 창 밖을 누르거나 Esc로
 * 닫는다. 화면 폭(412px 폰)에 맞춰 좌우 16px 안으로 잘라 둔다.
 */
export function toggleItemTip(anchor: HTMLElement, item: EquipDef): void {
  if (open && open.anchor === anchor) {
    closeItemTip();
    return;
  }
  closeItemTip();
  const where =
    item.source === Source.Season
      ? '영토전 시즌 보상(금 등급)'
      : isLoot(item)
        ? `전리품 · 깊이 ${item.minDepth}부터`
        : `상점 · ${item.cost.toLocaleString()}냥`;
  const box = el(
    'div',
    { class: `item-tip t${item.tier}`, role: 'tooltip', id: 'item-tip' },
    el(
      'div',
      { class: 'tip-head' },
      itemIcon(item, 64),
      el(
        'div',
        { class: 'tip-title' },
        el('b', {}, item.name),
        el('span', { class: 'dim' }, item.hanja),
        el('span', { class: 'tip-meta' }, `T${item.tier} · ${SLOT_NAMES[item.slot]} · ${where}`),
        item.culture !== null ? el('span', { class: 'tip-meta' }, `${CULTURE_NAMES[item.culture]} 전용`) : null,
      ),
    ),
    el('p', { class: 'tip-mods' }, modSummary(item)),
    item.note ? el('p', { class: 'tip-note' }, item.note) : null,
    item.metricBlind ? el('p', { class: 'tip-blind' }, `※ ${item.metricBlind}`) : null,
  );
  const host = anchor.closest('#screen') ?? document.body;
  host.append(box);
  // 포커스는 옮기지 않는다(단추에 남는다). 그래서 대화 상자가 아니라 설명(tooltip)이고, 단추가 가리킨다.
  anchor.setAttribute('aria-describedby', 'item-tip');
  anchor.setAttribute('aria-expanded', 'true');
  const place = (): void => {
    const r = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(320, vw - 32);
    box.style.width = `${w}px`;
    box.style.left = `${Math.max(16, Math.min(vw - 16 - w, r.left))}px`;
    const h = box.offsetHeight;
    const below = r.bottom + 6;
    box.style.top = `${below + h <= vh - 8 ? below : Math.max(8, r.top - 6 - h)}px`;
  };
  place();
  const onDown = (e: Event): void => {
    const t = e.target as Node;
    if (box.contains(t) || anchor.contains(t)) return;
    closeItemTip();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeItemTip();
  };
  const onScroll = (): void => closeItemTip();
  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('keydown', onKey);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onScroll);
  open = {
    box,
    anchor,
    off: () => {
      anchor.removeAttribute('aria-describedby');
      anchor.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    },
  };
}

/** 아이콘과 이름을 한 단추로: 누르면 설명 창. 줄의 다른 단추(차기·팔기)와 따로 눌린다. */
export function itemTipButton(item: EquipDef, iconSize = 32, label?: string): HTMLElement {
  const b = el(
    'button',
    { type: 'button', class: 'item-tipbtn', 'aria-label': `${item.name} 설명 보기`, 'aria-expanded': 'false' },
    itemIcon(item, iconSize),
    label !== undefined ? el('span', { class: 'tname' }, label) : null,
  );
  b.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleItemTip(b, item);
  });
  return b;
}
